import { FunctionComponent, useEffect, useState } from 'react'
import { Edit, ExpandMore, Save } from '@mui/icons-material'
import styles from './mealHandler.module.scss'
import { useTranslation } from 'react-i18next'
import { MealDetail, MealType, MealTypeDetail } from '@/types/Meal'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import MealDetailRow, { getMealDetailKey } from './mealDetailRow.tsx'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper'

interface MealTypeCardProps {
    type: MealType
    typeDetails: MealTypeDetail[]
    canUpdate: boolean
    mealTypeTranslation: string
    getOrderAdjustment: (detail: MealDetail, contextKey?: string) => number
    handleAdjustOrder: (detail: MealDetail, delta: number, contextKey?: string) => void
    handleFreezeMealType: (type: string, sectionIds: number[]) => void
}

function sortDetails(a: MealDetail, b: MealDetail): number {
    const aComb: string =
        a.combination.menus.join(',').toLowerCase() +
        '-' +
        a.combination.habits.join(',').toLowerCase() +
        '-' +
        a.combination.textures.join(',').toLowerCase()
    const bComb: string =
        b.combination.menus.join(',').toLowerCase() +
        '-' +
        b.combination.habits.join(',').toLowerCase() +
        '-' +
        b.combination.textures.join(',').toLowerCase()

    if (aComb < bComb) return -1
    if (aComb > bComb) return 1
    return b.quantityReserved.length - a.quantityReserved.length
}

function aggregateTypeDetails(typeDetails: MealTypeDetail[]): {
    details: MealDetail[]
    frozen: boolean
    mixedFrozen: boolean
    frozenDate: string | null
} {
    const detailMap: Map<string, MealDetail> = new Map()
    for (const td of typeDetails) {
        for (const detail of td.details) {
            const key: string = getMealDetailKey(detail)
            const existing: MealDetail | undefined = detailMap.get(key)
            if (existing) {
                detailMap.set(key, {
                    ...existing,
                    quantityAdded: existing.quantityAdded + detail.quantityAdded,
                    quantityReserved: [...existing.quantityReserved, ...detail.quantityReserved],
                    quantityReal: [...existing.quantityReal, ...detail.quantityReal]
                })
            } else {
                detailMap.set(key, { ...detail })
            }
        }
    }

    const anyFrozen: boolean = typeDetails.some((td: MealTypeDetail): boolean => td.frozen)
    const allFrozen: boolean = typeDetails.every((td: MealTypeDetail): boolean => td.frozen)
    const frozen: boolean = allFrozen
    const mixedFrozen: boolean = anyFrozen && !allFrozen
    const frozenDate: string | null = frozen
        ? typeDetails.reduce<string | null>((latest: string | null, td: MealTypeDetail): string | null => {
              if (!td.frozenDate) return latest
              if (!latest) return td.frozenDate
              return td.frozenDate > latest ? td.frozenDate : latest
          }, null)
        : null

    return {
        details: [...detailMap.values()].sort(
            (a: MealDetail, b: MealDetail): number => b.quantityReserved.length - a.quantityReserved.length
        ),
        frozen,
        mixedFrozen,
        frozenDate
    }
}

const MealTypeCard: FunctionComponent<MealTypeCardProps> = ({
    type,
    typeDetails,
    canUpdate,
    mealTypeTranslation,
    getOrderAdjustment,
    handleAdjustOrder,
    handleFreezeMealType
}: MealTypeCardProps) => {
    const { t } = useTranslation()
    const [expanded, setExpanded] = useState<boolean>(true)

    const { details, frozen, mixedFrozen, frozenDate } = aggregateTypeDetails(typeDetails)
    const sectionIds: number[] = typeDetails.map((td: MealTypeDetail): number => td.sectionId)
    const canCollapse: boolean = !mixedFrozen

    function getAggregatedOrderAdjustment(detail: MealDetail): number {
        return typeDetails.reduce((sum: number, td: MealTypeDetail): number => {
            const contextKey: string = `${type}_${td.sectionId}`
            return sum + getOrderAdjustment(detail, contextKey)
        }, 0)
    }

    useEffect((): void => {
        if (mixedFrozen) setExpanded(true)
    }, [mixedFrozen])

    return (
        <div className={`${styles.MealTypeCard} ${!expanded && frozen ? styles.MealTypeCardFrozen : ''}`}>
            <div
                className={styles.MealTypeHeader}
                onClick={() => {
                    if (canCollapse) setExpanded((prev) => !prev)
                }}
                style={canCollapse ? undefined : { cursor: 'default' }}
            >
                <span className={styles.MealTypeTitle}>{mealTypeTranslation}</span>
                <div className={styles.MealTypeHeaderActions}>
                    {!expanded && frozen && <Save className={styles.MealTypeFrozenIcon} />}
                    {canCollapse && (
                        <ExpandMore
                            className={`${styles.MealTypeToggle} ${!expanded ? styles.MealTypeToggleCollapsed : ''}`}
                        />
                    )}
                </div>
            </div>

            {expanded ? (
                <div className={styles.MealTypeContent}>
                    {typeDetails.map((td: MealTypeDetail) => {
                        const contextKey: string = `${type}_${td.sectionId}`
                        return (
                            <div
                                key={`${type}_${td.sectionId}`}
                                className={`${styles.SectionSubCard} ${td.frozen ? styles.SectionSubCardFrozen : ''}`}
                            >
                                <div className={styles.SectionSubHeader}>
                                    <span className={styles.SectionSubTitle}>{td.sectionName}</span>
                                    {td.frozen && <Save className={styles.SectionSubFrozenIcon} />}
                                </div>
                                <div className={styles.SectionSubContent}>
                                    {[...td.details].sort(sortDetails).map((detail: MealDetail) => (
                                        <MealDetailRow
                                            key={getMealDetailKey(detail)}
                                            detail={detail}
                                            canUpdate={canUpdate}
                                            mealFrozen={td.frozen}
                                            orderAdjustment={getOrderAdjustment(detail, contextKey)}
                                            onAdjustOrder={(delta: number) =>
                                                handleAdjustOrder(detail, delta, contextKey)
                                            }
                                        />
                                    ))}
                                    {canUpdate && (
                                        <div className={styles.ButtonContainer}>
                                            <ButtonTextIcon
                                                type={'custom'}
                                                customText={td.frozen ? t('unfreezeMeal') : t('frozenMeal')}
                                                customIcon={td.frozen ? <Edit /> : <Save />}
                                                className={td.frozen ? styles.ButtonFrozen : styles.Button}
                                                onClick={() => handleFreezeMealType(type, [td.sectionId])}
                                            />
                                        </div>
                                    )}
                                    {td.frozen && td.frozenDate && (
                                        <div className={styles.TextContainer}>
                                            {t('frozenOn')}
                                            {format(
                                                parse(td.frozenDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                                'EEEE dd MMMM yyyy - HH:mm',
                                                { locale: getLocalFromLanguage() }
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className={`${styles.MealTypeContent} ${styles.MealTypeContentCollapsed}`}>
                    <>
                        {details.map((detail: MealDetail) => (
                            <MealDetailRow
                                key={getMealDetailKey(detail)}
                                detail={detail}
                                canUpdate={false}
                                mealFrozen={frozen}
                                orderAdjustment={getAggregatedOrderAdjustment(detail)}
                                onAdjustOrder={() => {}}
                            />
                        ))}
                        {canUpdate && (
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    customText={frozen ? t('unfreezeMeal') : t('frozenMeal')}
                                    customIcon={frozen ? <Edit /> : <Save />}
                                    className={frozen ? styles.ButtonFrozen : styles.Button}
                                    onClick={() => handleFreezeMealType(type, sectionIds)}
                                />
                            </div>
                        )}
                        {frozen && frozenDate && (
                            <div className={styles.TextContainer}>
                                {t('frozenOn')}
                                {format(
                                    parse(frozenDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                    'EEEE dd MMMM yyyy - HH:mm',
                                    { locale: getLocalFromLanguage() }
                                )}
                            </div>
                        )}
                    </>
                </div>
            )}
        </div>
    )
}

export default MealTypeCard
